import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Paper, Typography, Stack } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import { sectionBackgrounds, resolveGradient } from '../../../theme';
function ContentRow({ icon, label, value, href, textColor, secondaryColor, accentColor, fontSize, fontFamily, fontWeight, iconBgColor, iconColor, }) {
    return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 2 }, children: [_jsx(Box, { sx: {
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: iconBgColor || 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: iconColor || 'text.primary',
                    flexShrink: 0,
                }, children: icon }), _jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { variant: "caption", sx: {
                            color: secondaryColor || 'text.secondary',
                            display: 'block',
                            mb: 0.5,
                            fontFamily: fontFamily || undefined,
                        }, children: label }), _jsx(Typography, { component: href ? 'a' : 'div', href: href, sx: {
                            fontWeight: Number(fontWeight) || 500,
                            fontSize: fontSize || '1rem',
                            fontFamily: fontFamily || undefined,
                            color: textColor || 'text.primary',
                            textDecoration: 'none',
                            '&:hover': href ? { color: accentColor || 'primary.main' } : {},
                        }, children: value })] })] }));
}
/**
 * ContactCard – display contact information (name, phone, email, address, website).
 * Supports label + gradient (same as other blocks); props match flow.json structure.
 */
const ContactCard = ({ label, gradient, name, phone, email, address, website, padding = 3, borderRadius = 3, border, borderWidth, borderColor, borderStyle, boxShadow, backgroundColor, accentColor, textColor, secondaryColor, fontSize, fontFamily, fontWeight = 700, paddingTop, paddingBottom, paddingLeft, paddingRight, marginTop, marginBottom, maxWidth, width, componentAlignment = 'left', transition = 'all 0.3s ease', }) => {
    const background = gradient
        ? resolveGradient(gradient, sectionBackgrounds.hero)
        : backgroundColor || 'transparent';
    const iconBg = accentColor || backgroundColor || 'grey.200';
    const iconColor = textColor || 'text.primary';
    return (_jsx(Box, { component: "section", sx: {
            background,
            paddingTop: paddingTop || 3,
            paddingBottom: paddingBottom || 3,
            paddingLeft: paddingLeft || 0,
            paddingRight: paddingRight || 0,
            marginTop: marginTop || 0,
            marginBottom: marginBottom || 0,
            textAlign: componentAlignment,
        }, children: _jsxs(Box, { sx: { maxWidth: maxWidth || 480, mx: 'auto', px: 2 }, children: [label && (_jsx(Typography, { variant: "overline", sx: { display: 'block', mb: 2, color: 'text.secondary', letterSpacing: 1 }, children: label })), _jsxs(Paper, { sx: {
                        p: padding,
                        borderRadius: typeof borderRadius === 'number' ? borderRadius : borderRadius || 3,
                        border: border || '1px solid',
                        borderColor: borderColor || 'divider',
                        borderWidth: borderWidth || undefined,
                        borderStyle: borderStyle || undefined,
                        boxShadow: boxShadow || '0 1px 3px rgba(0,0,0,0.08)',
                        backgroundColor: backgroundColor || 'background.paper',
                        width: width || '100%',
                        transition,
                        '&:hover': {
                            boxShadow: boxShadow || '0 4px 12px rgba(0,0,0,0.1)',
                        },
                    }, children: [name && (_jsx(Typography, { sx: {
                                fontSize: fontSize || '1.5rem',
                                fontWeight: Number(fontWeight) || 700,
                                fontFamily: fontFamily || undefined,
                                mb: 3,
                                color: textColor || 'text.primary',
                            }, children: name })), _jsxs(Stack, { spacing: 2.5, children: [phone && (_jsx(ContentRow, { icon: _jsx(PhoneIcon, {}), label: "Phone", value: phone, href: `tel:${phone.replace(/\D/g, '')}`, textColor: textColor, secondaryColor: secondaryColor, accentColor: accentColor, fontSize: fontSize, fontFamily: fontFamily, fontWeight: fontWeight, iconBgColor: iconBg, iconColor: iconColor })), email && (_jsx(ContentRow, { icon: _jsx(EmailIcon, {}), label: "Email", value: email, href: `mailto:${email}`, textColor: textColor, secondaryColor: secondaryColor, accentColor: accentColor, fontSize: fontSize, fontFamily: fontFamily, fontWeight: fontWeight, iconBgColor: iconBg, iconColor: iconColor })), address && (_jsx(ContentRow, { icon: _jsx(LocationOnIcon, {}), label: "Address", value: address, textColor: textColor, secondaryColor: secondaryColor, accentColor: accentColor, fontSize: fontSize, fontFamily: fontFamily, fontWeight: fontWeight, iconBgColor: iconBg, iconColor: iconColor })), website && (_jsx(ContentRow, { icon: _jsx(LanguageIcon, {}), label: "Website", value: website, href: website.startsWith('http') ? website : `https://${website}`, textColor: textColor, secondaryColor: secondaryColor, accentColor: accentColor, fontSize: fontSize, fontFamily: fontFamily, fontWeight: fontWeight, iconBgColor: iconBg, iconColor: iconColor }))] })] })] }) }));
};
export default ContactCard;
